import { useRef, useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, Upload, X, RotateCcw, SwitchCamera } from 'lucide-react';
import { Language } from '@/shared/types';

interface PhotoFieldProps {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  language: Language;
  label?: string;
  required?: boolean;
}

const labels = {
  takePhoto: { fr: 'Prendre une photo', en: 'Take a photo', es: 'Tomar una foto' },
  uploadPhoto: { fr: 'Glisser une photo ici ou cliquer', en: 'Drop a photo here or click', es: 'Arrastra una foto aquí o haz clic' },
  retake: { fr: 'Reprendre', en: 'Retake', es: 'Repetir' },
  remove: { fr: 'Supprimer', en: 'Remove', es: 'Eliminar' },
  cameraError: { fr: "Impossible d'accéder à la caméra", en: 'Cannot access camera', es: 'No se puede acceder a la cámara' },
};

export function PhotoField({ value, onChange, language, label, required }: PhotoFieldProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [streaming, setStreaming] = useState(false);
  const [streamRef, setStreamRef] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState(false);
  const [pendingStream, setPendingStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  useEffect(() => {
    if (streaming && pendingStream && videoRef.current) {
      videoRef.current.srcObject = pendingStream;
      videoRef.current.play().catch(() => {});
      setPendingStream(null);
    }
  }, [streaming, pendingStream]);

  useEffect(() => {
    return () => { streamRef?.getTracks().forEach(t => t.stop()); };
  }, [streamRef]);

  const startCamera = async (mode: 'user' | 'environment' = facingMode) => {
    setCameraError(false);
    streamRef?.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setStreamRef(stream);
      setPendingStream(stream);
      setStreaming(true);
    } catch {
      setCameraError(true);
    }
  };

  const flipCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    startCamera(newMode);
  };

  const stopCamera = () => {
    streamRef?.getTracks().forEach(t => t.stop());
    setStreamRef(null);
    setPendingStream(null);
    setStreaming(false);
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    onChange(canvas.toDataURL('image/jpeg', 0.85));
    stopCamera();
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  }, [onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    noClick: false,
  });

  return (
    <div className="space-y-3">
      {label && (
        <label className="text-base font-medium flex items-center gap-2">
          {label}
          {required && <span className="text-destructive text-sm">*</span>}
        </label>
      )}

      {value && !streaming ? (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border-2 border-border">
            <img src={value} alt="Captured" className="w-full max-h-80 object-contain bg-muted" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => { onChange(null); startCamera(); }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-secondary text-secondary-foreground rounded-xl text-sm font-medium">
              <RotateCcw size={16} /> {labels.retake[language]}
            </button>
            <button type="button" onClick={() => onChange(null)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-destructive/10 text-destructive rounded-xl text-sm font-medium">
              <X size={16} /> {labels.remove[language]}
            </button>
          </div>
        </div>
      ) : streaming ? (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border-2 border-primary bg-black">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-80 object-contain" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={capture}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-4 bg-primary text-primary-foreground rounded-xl font-semibold">
              <Camera size={20} /> {labels.takePhoto[language]}
            </button>
            <button type="button" onClick={flipCamera}
              className="px-4 py-4 bg-secondary text-secondary-foreground rounded-xl"
              title={language === 'fr' ? 'Changer de caméra' : 'Switch camera'}>
              <SwitchCamera size={20} />
            </button>
            <button type="button" onClick={stopCamera}
              className="px-4 py-4 bg-secondary text-secondary-foreground rounded-xl">
              <X size={20} />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <button type="button" onClick={() => startCamera()}
            className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-primary/30 hover:border-primary/60 rounded-xl text-primary font-medium transition-colors">
            <Camera size={24} />
            {labels.takePhoto[language]}
          </button>
          {cameraError && (
            <p className="text-sm text-destructive text-center">{labels.cameraError[language]}</p>
          )}
          <div
            {...getRootProps()}
            className={`flex items-center justify-center gap-3 p-6 border-2 border-dashed rounded-xl font-medium transition-colors cursor-pointer
              ${isDragActive
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border hover:border-primary/40 text-muted-foreground hover:text-primary'}`}
          >
            <input {...getInputProps()} />
            <Upload size={24} />
            {labels.uploadPhoto[language]}
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
